import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Font,
  Image,
} from "@react-pdf/renderer";
import dayjs from "dayjs";

Font.register({
  family: "Manrope",
  fonts: [
    { src: "/fonts/Manrope-Regular.ttf", fontWeight: "normal" },
    { src: "/fonts/Manrope-Bold.ttf", fontWeight: "bold" },
    { src: "/fonts/Manrope-Medium.ttf", fontWeight: "medium" },
  ],
});

const COL = {
  empId: 0.7,
  name: 1.6,
  jobTitle: 1.4,
  department: 1.2,
  division: 1.4,
  time: 1.0,
  joinDate: 1.0,
  status: 0.8,
  nationality: 1.0,
  mobile: 1.0,
};

const styles = StyleSheet.create({
  page: {
    padding: 20,
    paddingBottom: 90,
    fontSize: 10,
    fontFamily: "Manrope",
  },
  headerSpace: {
    height: 80,
    // borderBottomWidth: 1,
    borderBottomColor: "#ccc",
    marginBottom: 10,
  },
  header_image: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
  },
  centerDiv: {
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: 12,
  },
  mainTitle: {
    fontSize: 14,
    fontWeight: "bold",
    marginBottom: 4,
  },
  smallText: {
    fontSize: 9,
    color: "grey",
    marginBottom: 8,
  },
  tableHeader: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#000",
    paddingVertical: 5,
    paddingHorizontal: 8,
    backgroundColor: "#2663EB",
    color: "#ffffff",
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#eee",
    paddingVertical: 4,
    alignItems: "flex-start",
  },
  cell: {
    fontSize: 8,
  },
  cellRight: {
    fontSize: 8,
    textAlign: "right",
  },
  cellBold: {
    fontSize: 8,
    fontWeight: "bold",
  },
  cellBoldRight: {
    fontSize: 8,
    fontWeight: "bold",
    textAlign: "right",
  },
  cellGreen: {
    fontSize: 8,
    color: "green",
  },
  cellRed: {
    fontSize: 8,
    color: "red",
  },
  //   footerSpace: {
  //     position: "absolute",
  //     bottom: 24,
  //     left: 0,
  //     right: 0,
  //     height: 60,
  //   },
  //   footer_image: {
  //     width: "100%",
  //     height: 60,
  //     objectFit: "cover",
  //   },
  //   footerRow: {
  //     position: "absolute",
  //     bottom: 88,
  //     left: 20,
  //     right: 20,
  //     flexDirection: "row",
  //     justifyContent: "center",
  //     borderTopWidth: 1,
  //     borderTopColor: "#ccc",
  //     paddingTop: 5,
  //   },
  //   footer: {
  //     fontSize: 9,
  //     color: "grey",
  //   },
  footerPageNum: {
    position: "absolute",
    bottom: 6,
    left: 0,
    right: 0,
    textAlign: "center",
    fontSize: 9,
    color: "grey",
  },

  // Footer image sits just above the page number
  footerSpace: {
    position: "absolute",
    bottom: 22,
    left: 0,
    right: 0,
    height: 80,
  },
  footer_image: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
});

const MedicalLeaveTrackingDocument = ({ medicalLeaveTrackingPrintData }) => {
  console.log("medicalLeaveTrackingPrintData:", medicalLeaveTrackingPrintData);

  const bondTimeportNoData = JSON.parse(
    localStorage.getItem("BondTimeportNoData") || "[]",
  );

  const headerImage = bondTimeportNoData[0]?.REP_HEADER;
  const footerImage = bondTimeportNoData[0]?.REP_FOOTER;

  const totalCount = medicalLeaveTrackingPrintData?.length || 0;

  const columns = [
    { key: "EMP ID", flex: COL.empId, bold: false },
    { key: "NAME", flex: COL.name, bold: true },
    { key: "JOB TITLE", flex: COL.jobTitle, bold: false },
    { key: "DEPARTMENT", flex: COL.department, bold: false },
    { key: "DIVISION", flex: COL.division, bold: false },
  ];

  const groupedData = medicalLeaveTrackingPrintData?.reduce((acc, r) => {
    const key = r["EMP ID"];
    if (!acc[key]) {
      acc[key] = {
        ...r, // common fields
        punches: [], // repeated fields
      };
    }
    acc[key].punches.push({
      Total_Days: r.Total_Days,
      "Ticket Status": r['Ticket Status'],
      ApprovalStatus: r.ApprovalStatus,
      "Leave Type": r["Leave Type"],
    });
    return acc;
  }, {});

  const groupedRows = Object.values(groupedData || {});
  const fmt = (val, format) => {
    const d = dayjs(val);
    return d.isValid() ? d.format(format) : "-";
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.headerSpace} fixed>
          {headerImage && (
            <Image src={headerImage} style={styles.header_image} />
          )}
        </View>

        {/* TITLE */}
        <View style={styles.centerDiv}>
          <Text style={styles.mainTitle}>Medical Leave Tracking Report</Text>
          <Text style={styles.smallText}>
            Total: {medicalLeaveTrackingPrintData?.length || 0} records
          </Text>
        </View>

        <View style={styles.tableHeader}>
          <View style={{ flex: 0.6 }}>
            <Text style={styles.cellBold}>Sl Code</Text>
          </View>

          <View style={{ flex: 0.9 }}>
            <Text style={styles.cellBold}>Emp ID</Text>
          </View>

          <View style={{ flex: 1.8 }}>
            <Text style={styles.cellBold}>Name</Text>
          </View>

          <View style={{ flex: 1.6 }}>
            <Text style={styles.cellBold}>Leave Type</Text>
          </View>

          <View style={{ flex: 1.5 }}>
            <Text style={styles.cellBold}>Application Date</Text>
          </View>

          <View style={{ flex: 1.3 }}>
            <Text style={styles.cellBold}>From Date</Text>
          </View>

          <View style={{ flex: 1.3 }}>
            <Text style={styles.cellBold}>To Date</Text>
          </View>

          <View style={{ flex: 1.4 }}>
            <Text style={styles.cellBold}>Leave Category</Text>
          </View>

          <View style={{ flex: 1.2 }}>
            <Text style={styles.cellBold}>Status</Text>
          </View>

          <View style={{ flex: 1.0 }}>
            <Text style={styles.cellBold}>Ticket</Text>
          </View>

          <View style={{ flex: 1.0 }}>
            <Text style={styles.cellBoldRight}>Total Days</Text>
          </View>
        </View>

        {groupedRows?.map((r, idx) => (
          <View key={idx} style={styles.tableRow}>
            {/* Common fields */}
            <View style={{ flex: 0.5 }}>
              <Text style={styles.cell}>{r.serialcode || "-"}</Text>
            </View>
            <View style={{ flex: 0.5 }}>
              <Text style={styles.cell}>{r['EMP ID'] || "-"}</Text>
            </View>

            <View style={{ flex: 1.0 }}>
              <Text style={styles.cellBold}>{r.NAME || "-"}</Text>
            </View>
            {/* <View style={{ flex: 1.5 }}>
              <Text style={styles.cell}>{r.JobTitle || "-"}</Text>
            </View>
            <View style={{ flex: 1.5 }}>
              <Text style={styles.cell}>{r.Division || "-"}</Text>
            </View> */}

            {/* Leave rows */}
            <View style={{ flex: 6, flexDirection: "column" }}>
              {r.punches.map((p, i) => (
                <View
                  key={i}
                  style={{
                    flexDirection: "row",
                    paddingVertical: 1,
                  }}
                >
                  <View style={{ flex: 1.25 }}>
                    <Text style={styles.cell}>{p["Leave Type"] || "-"}</Text>
                  </View>

                  <View style={{ flex: 1 }}>
                    <Text style={styles.cell}>
                      {fmt(r?.ApplicationDate, "YYYY-MM-DD")}
                    </Text>
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.cell}>
                      {fmt(r["Leave From"], "YYYY-MM-DD")}
                    </Text>
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.cell}>
                      {fmt(r["Leave To"], "YYYY-MM-DD")}
                    </Text>
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.cell}>{r.Leave_Category}</Text>
                  </View>

                  <View style={{ flex: 1 }}>
                    <Text style={styles.cell}>{p.ApprovalStatus ?? "-"}</Text>
                  </View>
                  <View style={{ flex: 1.25 }}>
                    <Text style={styles.cell}>{p["Ticket Status"] ?? "-"}</Text>
                  </View>
                  <View style={{ flex: 0.5 }}>
                    <Text style={styles.cellRight}>{p['Total Days'] ?? 0}</Text>
                  </View>

                  {/* <View style={{ flex: 1 }}>
                    <Text style={styles.cell}>{p.TAKEN ?? 0}</Text>
                  </View>

                  <View style={{ flex: 1 }}>
                    <Text style={styles.cell}>{p.BALANCE ?? 0}</Text>
                  </View> */}

                  {/* <View style={{ flex: 1.2 }}>
                    <Text style={styles.cell}>
                      {p.EXPIRY_DATE
                        ? dayjs(p.EXPIRY_DATE).format("YYYY-MM-DD")
                        : "-"}
                    </Text>
                  </View> */}
                </View>
              ))}
            </View>
          </View>
        ))}

        <View style={styles.footerSpace} fixed>
          {footerImage && (
            <Image src={footerImage} style={styles.footer_image} />
          )}
        </View>

        {/* PAGE NUMBER — below the image */}
        <Text
          style={styles.footerPageNum}
          render={({ pageNumber, totalPages }) =>
            `Page ${pageNumber} of ${totalPages}`
          }
          fixed
        />
      </Page>
    </Document>
  );
};

export default MedicalLeaveTrackingDocument;
